<template>
  <div>
    <div class="toolbar">
      <el-select v-model="projectId" clearable filterable placeholder="筛选项目" style="width:220px" @change="onProjectChange">
        <el-option
          v-for="p in projects"
          :key="p.id"
          :label="p.name || p.customerName"
          :value="p.id"
        />
      </el-select>
      <el-select v-model="stageDefId" clearable filterable placeholder="筛选工序" style="width:180px" @change="load">
        <el-option v-for="s in stageDefs" :key="s.id" :label="s.name" :value="s.id" />
      </el-select>
      <el-select v-model="status" clearable placeholder="工序状态" style="width:130px" @change="load">
        <el-option label="未开始" value="PENDING" />
        <el-option label="进行中" value="DOING" />
        <el-option label="已完成" value="DONE" />
      </el-select>
      <el-select v-model="planSchedule" clearable placeholder="计划状态" style="width:130px" @change="load">
        <el-option label="正常" value="NORMAL" />
        <el-option label="延期" value="DELAYED" />
      </el-select>
      <el-button type="primary" icon="el-icon-search" @click="load">查询</el-button>
      <el-button icon="el-icon-download" :loading="exporting" :disabled="!list.length" @click="onExport">导出</el-button>
    </div>

    <el-table :data="pageList" border v-loading="loading">
      <el-table-column label="序号" width="60" align="center">
        <template slot-scope="{ $index }">{{ (page - 1) * pageSize + $index + 1 }}</template>
      </el-table-column>
      <el-table-column prop="projectName" label="项目" min-width="140" show-overflow-tooltip>
        <template slot-scope="{ row }">
          <project-name-link :project-id="row.projectId" :name="row.projectName" />
        </template>
      </el-table-column>
      <el-table-column prop="stageName" label="工序" min-width="110" show-overflow-tooltip />
      <el-table-column label="计划日期" width="200" show-overflow-tooltip>
        <template slot-scope="{ row }">
          {{ fmtRange(row.originPlanStart || row.planStart, row.originPlanEnd || row.planEnd) }}
        </template>
      </el-table-column>
      <el-table-column label="施工日期" width="200" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ actualPlanText(row) }}</template>
      </el-table-column>
      <el-table-column label="计划天数" width="90" align="center">
        <template slot-scope="{ row }">{{ planDays(row) }}</template>
      </el-table-column>
      <el-table-column label="施工天数" width="90" align="center">
        <template slot-scope="{ row }">{{ actualDays(row) }}</template>
      </el-table-column>
      <el-table-column prop="workerName" label="工人" width="100" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.workerName || '-' }}</template>
      </el-table-column>
      <el-table-column label="确认状态" width="110">
        <template slot-scope="{ row }">{{ row.workerStatusName || '-' }}</template>
      </el-table-column>
      <el-table-column label="工序状态" width="80" fixed="right">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="statusType(stageStatusMap, row.status || 'PENDING')">
            {{ statusLabel(stageStatusMap, row.status || 'PENDING') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="计划状态" width="80" fixed="right">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="planScheduleStatus(row) === '延期' ? 'warning' : 'success'">
            {{ planScheduleStatus(row) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="60" fixed="right" align="center">
        <template slot-scope="{ row }">
          <el-button type="text" @click="goDetail(row)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>
    <list-pagination
      :total="listTotal"
      :page="page"
      :page-size="pageSize"
      @current-change="onPageChange"
      @size-change="onPageSizeChange"
    />
  </div>
</template>

<script>
import { listProjectStages, listProjects, listStageDefs, exportProjectStages } from '../../api/project'
import { stageStatusMap, statusLabel, statusType } from '../../utils/status'
import ListPagination from '../../components/ListPagination.vue'
import ProjectNameLink from '../../components/ProjectNameLink.vue'
import listPaging from '../../mixins/listPaging'

export default {
  name: 'ProjectStage',
  components: { ListPagination, ProjectNameLink },
  mixins: [listPaging],
  data() {
    return {
      stageStatusMap,
      projectId: null,
      stageDefId: null,
      status: '',
      planSchedule: '',
      projects: [],
      stageDefs: [],
      list: [],
      loading: false,
      exporting: false
    }
  },
  created() {
    this.applyRouteQuery()
    this.loadProjects()
    this.loadStageDefs()
    this.load()
  },
  watch: {
    '$route.query.projectId'() {
      const next = this.parseProjectId(this.$route.query.projectId)
      if (next !== this.projectId) {
        this.projectId = next
        this.load()
      }
    }
  },
  methods: {
    statusLabel,
    statusType,
    parseProjectId(v) {
      if (v == null || v === '') return null
      const id = Number(v)
      return Number.isNaN(id) ? null : id
    },
    applyRouteQuery() {
      this.projectId = this.parseProjectId(this.$route.query.projectId)
    },
    onProjectChange() {
      const query = { ...this.$route.query }
      if (this.projectId) query.projectId = String(this.projectId)
      else delete query.projectId
      this.$router.replace({ path: '/project/stages', query }).catch(() => {})
      this.load()
    },
    fmtDate(v) {
      if (!v) return '-'
      const s = String(v)
      return s.length >= 10 ? s.slice(0, 10) : s
    },
    fmtRange(a, b) {
      const s = this.fmtDate(a)
      const e = this.fmtDate(b)
      if (s === '-' && e === '-') return '-'
      return s + ' ~ ' + e
    },
    actualPlanText(row) {
      if (!row) return '-'
      return this.fmtRange(row.planStart, row.planEnd)
    },
    daySpan(start, end) {
      const s = this.fmtDate(start)
      const e = this.fmtDate(end)
      if (s === '-' || e === '-') return '-'
      const a = new Date(s.replace(/-/g, '/'))
      const b = new Date(e.replace(/-/g, '/'))
      if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '-'
      const days = Math.round((b - a) / 86400000) + 1
      return days > 0 ? days : '-'
    },
    planDays(row) {
      return this.daySpan(row.originPlanStart || row.planStart, row.originPlanEnd || row.planEnd)
    },
    actualDays(row) {
      return this.daySpan(row.planStart, row.planEnd)
    },
    /** 施工开始不为空且晚于计划开始 → 延期，否则正常 */
    planScheduleStatus(row) {
      if (!row || !row.planStart) return '正常'
      const constructionStart = this.fmtDate(row.planStart)
      const planStart = this.fmtDate(row.originPlanStart || row.planStart)
      if (constructionStart === '-' || planStart === '-') return '正常'
      return constructionStart > planStart ? '延期' : '正常'
    },
    goDetail(row) {
      if (!row || !row.id) {
        return this.$message.warning('缺少计划信息')
      }
      this.$router.push({
        path: '/project/stages/detail',
        query: { id: String(row.id) }
      })
    },
    async loadProjects() {
      const res = await listProjects({})
      this.projects = res.data || []
    },
    async loadStageDefs() {
      const res = await listStageDefs({})
      this.stageDefs = ((res.data || []).filter(s => s.enabled !== 0))
    },
    async load() {
      this.loading = true
      try {
        const res = await listProjectStages(this.queryParams())
        this.list = res.data || []
        this.page = 1
      } finally {
        this.loading = false
      }
    },
    queryParams() {
      return {
        projectId: this.projectId || undefined,
        stageDefId: this.stageDefId || undefined,
        status: this.status || undefined,
        planScheduleStatus: this.planSchedule || undefined
      }
    },
    async onExport() {
      if (!this.list.length) {
        return this.$message.warning('暂无数据可导出')
      }
      this.exporting = true
      try {
        const res = await exportProjectStages(this.queryParams())
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = '项目计划.xlsx'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar { display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
</style>
